import React, { useMemo } from "react"
import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { Form, FormFeedback, Label, Button, Row, Col } from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import { useShowrooms } from "hooks/showroomHook"
import {
  useCreateCarCareServiceCenter,
  useEditCarCareServiceCenter,
} from "hooks/useCarCareServiceCenter"

const STATUS_OPTIONS = [
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
]

const CarCareServiceCenterAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  editedData,
}) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateCarCareServiceCenter()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditCarCareServiceCenter()

  const { data: showroomsData } = useShowrooms({ all: true })

  const showroomOptions = useMemo(() => {
    const list = Array.isArray(showroomsData?.data?.data)
      ? showroomsData.data.data
      : Array.isArray(showroomsData?.data)
      ? showroomsData.data
      : []
    return list.map(s => ({ value: s.id?.toString(), label: s.name }))
  }, [showroomsData])

  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Please enter the service center name")
      .max(255, "Name cannot exceed 255 characters"),
    email: Yup.string().email("Enter a valid email").nullable(),
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editedData?.name || "",
      short_name: editedData?.short_name || "",
      slogan: editedData?.slogan || "",
      showroom_id: editedData?.showroom_id?.toString() || "",
      address: editedData?.address || "",
      phone: editedData?.phone || "",
      alt_phone: editedData?.alt_phone || "",
      email: editedData?.email || "",
      payment_info: editedData?.payment_info || "",
      logo: editedData?.logo || "",
      is_active:
        editedData?.is_active === undefined
          ? "1"
          : editedData.is_active
          ? "1"
          : "0",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          ...values,
          showroom_id: values.showroom_id || null,
          is_active: values.is_active === "1",
        }

        const action = isEdit
          ? editMutation({ id: editedData.id, ...payload })
          : createMutation(payload)

        const res = await action
        toast.success(
          res?.message ??
            `Service center ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        toast.error(
          error?.response?.data?.message ?? "Failed to save service center."
        )
      }
    },
  })

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Service Center"}${
      loading ? "..." : ""
    }`
  }, [isEdit, editLoading, createLoading])

  const textField = (name, label, type = "text") => (
    <div className="mb-3">
      <Label htmlFor={name}>{label}</Label>
      <InputField
        type={type}
        name={name}
        id={name}
        value={formik.values[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        invalid={formik.touched[name] && !!formik.errors[name]}
      />
      {formik.touched[name] && formik.errors[name] && (
        <FormFeedback type="invalid">{formik.errors[name]}</FormFeedback>
      )}
    </div>
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Service Center" : "Add Service Center"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <div className="mb-3">
          <Label htmlFor="name">
            Name <span className="text-danger">*</span>
          </Label>
          <InputField
            type="text"
            name="name"
            id="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched.name && !!formik.errors.name}
          />
          {formik.touched.name && formik.errors.name && (
            <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
          )}
        </div>

        <Row>
          <Col md={6}>{textField("short_name", "Short Name")}</Col>
          <Col md={6}>{textField("slogan", "Slogan")}</Col>
        </Row>

        <FormikCreatableSelect
          name="showroom_id"
          label="Branch / Showroom"
          formik={formik}
          options={showroomOptions}
          placeholder="Select branch (optional)"
        />

        {textField("address", "Address", "textarea")}

        <Row>
          <Col md={6}>{textField("phone", "Phone")}</Col>
          <Col md={6}>{textField("alt_phone", "Alternate Phone")}</Col>
        </Row>

        {textField("email", "Email")}
        {textField("payment_info", "Payment Information", "textarea")}
        {textField("logo", "Logo URL")}

        <FormikCreatableSelect
          name="is_active"
          label="Status"
          formik={formik}
          options={STATUS_OPTIONS}
        />

        <div className="text-end mt-3">
          <Button
            color="success"
            type="submit"
            disabled={editLoading || createLoading}
          >
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default CarCareServiceCenterAddEdit
